// hooks/useNotifications.ts
import { useState, useEffect, useRef } from "react";
import NotificationService, {
  Notification,
} from "@services/NotificationService";
import { wsBaseUrl } from "./baseValues";
import { pushToasts } from "@shared/components/Notification/components/PushToasts";
import { useNavigate } from "react-router-dom";

export interface NotificationsContextType {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  unreadCount: number;
  fetchNotifications: (pageToLoad?: number, append?: boolean) => Promise<void>;
  loadMoreNotifications: () => void;
  hasMore: boolean;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  resetNotifications: () => Promise<void>;
}

const useNotifications = (): NotificationsContextType => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const socketRef = useRef<WebSocket | null>(null);
  const navigate = useNavigate();

  const fetchNotifications = async (pageToLoad = 1, append = false) => {
    try {
      const data = await NotificationService.fetchNotifications(pageToLoad);
      setNotifications((prev) => (append ? [...prev, ...data] : data));
      setUnreadCount((prev) =>
        append
          ? prev + data.filter((n) => n.unread).length
          : data.filter((n) => n.unread).length
      );
      if (data.length < 20) setHasMore(false);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  const loadMoreNotifications = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage, true);
  };

  const reportReadyOnClick = (notification: Notification) => {
    navigate(
      `/report/${notification.object_id}/project/${notification.project_id}`
    );
  };

  useEffect(() => {
    fetchNotifications(); // initial fetch

    socketRef.current = new WebSocket(`${wsBaseUrl}/notifications`);
    socketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);

      fetchNotifications();

      if (data.type === "new_notification") {
        const notification = data.notification as Notification;
        if (notification.notification_type === "report_is_ready") {
          pushToasts.report_ready(notification.message, () =>
            reportReadyOnClick(notification)
          );
        }
        // when the following notification types are introduced
        else if (notification.notification_type === "project_shared") {
          pushToasts.project_shared(notification.message);
        } else if (notification.notification_type === "report_shared") {
          pushToasts.report_shared(notification.message);
        }
      }
    };
    return () => socketRef.current?.close();
  }, []);

  const markAsRead = async (id: number) => {
    try {
      await NotificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, unread: false } : n))
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read", error);
    }
  };

  const resetNotifications = async () => {
    setPage(1);
    setHasMore(true);
    await fetchNotifications(1, false);
  };

  return {
    notifications,
    setNotifications,
    unreadCount,
    fetchNotifications,
    loadMoreNotifications,
    hasMore,
    markAsRead,
    markAllAsRead,
    resetNotifications,
  };
};

export default useNotifications;
